"use client";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined";
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import { useRouter } from "next/navigation";
import { useState } from "react";
import NotificationItem from "~/components/navbar/NotificationItem";
import { useNotifications } from "~/components/notifications/NotificationsContext";
import { trackEvent } from "~/connectors/analytics.connector";
import { AnalyticsEvents } from "~/connectors/analyticsEvents";
import { customerNotificationsService } from "~/connectors/notifications.connector";
import type { Notification } from "~/types";

import Backdrop from "@mui/material/Backdrop";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import Popover from "@mui/material/Popover";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

export default function NotificationBell() {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const router = useRouter();
  const { notifications, unreadCount, isLoading, refresh } = useNotifications();

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    refresh();
    trackEvent(AnalyticsEvents.NOTIFICATION_PANEL_OPEN, { unreadCount });
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDelete = async (notificationId: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    await customerNotificationsService.deleteNotification(notificationId);
    refresh();
  };

  const handleMarkAllRead = async () => {
    await customerNotificationsService.markAllAsRead();
    refresh();
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.isRead) {
      await customerNotificationsService.markAsRead(notification.id);
      refresh();
    }
    trackEvent(AnalyticsEvents.NOTIFICATION_BELL_CLICK, {
      notificationId: notification.id,
    });
    handleClose();
    if (notification.actionUrl && notification.actionUrl !== "/") {
      router.push(notification.actionUrl);
    }
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Tooltip title="Notificări">
        <IconButton
          onClick={handleOpen}
          aria-label="Notificări"
          color="inherit"
        >
          <Badge badgeContent={unreadCount} color="error" max={99}>
            {open ? (
              <NotificationsIcon fontSize="small" />
            ) : (
              <NotificationsNoneIcon fontSize="small" />
            )}
          </Badge>
        </IconButton>
      </Tooltip>
      <Backdrop
        open={open}
        onClick={handleClose}
        sx={{ zIndex: (theme) => theme.zIndex.modal - 1 }}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: 2,
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
              width: { xs: "92vw", sm: 420 },
              maxWidth: "96vw",
              maxHeight: { xs: 520, sm: 720 },
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            },
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: 1,
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Notificări
          </Typography>
          {unreadCount > 0 && (
            <Tooltip title="Marchează toate ca citite">
              <IconButton size="small" onClick={handleMarkAllRead}>
                <CheckCircleOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Body */}
        <Box sx={{ overflowY: "auto", overflowX: "hidden", flexGrow: 1 }}>
          {isLoading && !notifications.length ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <NotificationsNoneIcon
                sx={{ fontSize: 40, color: "text.disabled", mb: 1 }}
              />
              <Typography variant="body2" sx={{
                color: "text.secondary"
              }}>
                Nu ai notificări
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {notifications.map((notification, index) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onDelete={handleDelete}
                  onClick={handleNotificationClick}
                  showDivider={index < notifications.length - 1}
                />
              ))}
            </List>
          )}
        </Box>
      </Popover>
    </>
  );
}
